/**
 * WhatsApp Groups Domain API Route Definitions
 *
 * Native WhatsApp group lifecycle over the Meta Cloud API Groups capability:
 * create / list groups, manage the invite link, add / remove participants, and
 * moderate join requests (list / approve / reject) for admin-approval groups.
 *
 * Every operation is scoped to a `connectionId` — the org's standalone
 * META_WHATSAPP connection (one WhatsApp business number). The org comes from
 * the authenticated context; the connection is resolved tenant-safe server-side.
 *
 * Direct-Meta only and gated by META_NATIVE_DIRECT (end-game switch).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/** Whether new members join automatically or need admin approval. */
export const WHATSAPP_GROUP_JOIN_APPROVAL_MODES = ["auto_approve", "admin_approval"] as const;
export const WhatsAppGroupJoinApprovalModeSchema = z.enum(WHATSAPP_GROUP_JOIN_APPROVAL_MODES);

/** Identifies the META_WHATSAPP connection (business number) to operate as. */
const ConnectionIdField = z.string().min(1);

/** Cloud API limits: subject <= 128 chars, description <= 2048, <= 8 users/op. */
const CreateWhatsAppGroupBodySchema = z.object({
  connectionId: ConnectionIdField,
  subject: z.string().min(1).max(128),
  description: z.string().max(2048).optional(),
  joinApprovalMode: WhatsAppGroupJoinApprovalModeSchema.optional()
});

// Group creation is async — the Graph API acknowledges with a request_id and the
// real group id arrives later via the groups webhook.
const CreateWhatsAppGroupDataSchema = z.object({ requestId: z.string() });

const ListWhatsAppGroupsQuerySchema = z.object({
  connectionId: ConnectionIdField,
  limit: z.coerce.number().int().min(1).max(100).optional(),
  after: z.string().optional(),
  before: z.string().optional()
});

const WhatsAppGroupSummarySchema = z.object({
  id: z.string(),
  subject: z.string(),
  createdAt: z.string().optional()
});

const ListWhatsAppGroupsDataSchema = z.object({
  groups: z.array(WhatsAppGroupSummarySchema),
  paging: z.object({ before: z.string().optional(), after: z.string().optional() }).optional()
});

const WhatsAppGroupPathVarsSchema = z.object({ groupId: z.string().min(1) });

/** GET reads pass the connection in the query; mutations pass it in the body. */
const GroupConnectionQuerySchema = z.object({ connectionId: ConnectionIdField });
const GroupConnectionBodySchema = z.object({ connectionId: ConnectionIdField });

const InviteLinkDataSchema = z.object({ inviteLink: z.string() });
const RevokeInviteLinkDataSchema = z.object({ success: z.boolean() });

const GroupParticipantsBodySchema = z.object({
  connectionId: ConnectionIdField,
  users: z.array(z.string().min(1)).min(1).max(8)
});
const GroupParticipantsDataSchema = z.object({ message: z.string() });

// --- Join-request moderation (admin-approval groups) -----------------------

/** A pending join request awaiting admin moderation. */
const WhatsAppGroupJoinRequestSchema = z.object({
  joinRequestId: z.string(),
  waId: z.string(),
  creationTimestamp: z.number()
});

const ListJoinRequestsQuerySchema = z.object({
  connectionId: ConnectionIdField,
  limit: z.coerce.number().int().min(1).max(100).optional(),
  after: z.string().optional(),
  before: z.string().optional()
});

const ListJoinRequestsDataSchema = z.object({
  joinRequests: z.array(WhatsAppGroupJoinRequestSchema),
  paging: z.object({ before: z.string().optional(), after: z.string().optional() }).optional()
});

/** Approve / reject both take a non-empty list of join_request ids. */
const JoinRequestIdsBodySchema = z.object({
  connectionId: ConnectionIdField,
  joinRequestIds: z.array(z.string().min(1)).min(1)
});

const ApproveJoinRequestsDataSchema = z.object({
  approved: z.array(z.string()),
  failed: z.array(z.string()).optional()
});
const RejectJoinRequestsDataSchema = z.object({
  rejected: z.array(z.string()),
  failed: z.array(z.string()).optional()
});

export const ZWhatsAppGroups = {
  /** POST /api/whatsapp/groups — create a group (async; returns a request id). */
  Create: {
    method: "POST" as const,
    path: "/api/whatsapp/groups",
    Body: CreateWhatsAppGroupBodySchema,
    Response: ApiSuccessSchema(CreateWhatsAppGroupDataSchema)
  },

  /** GET /api/whatsapp/groups — list the connection's groups (cursor paged). */
  List: {
    method: "GET" as const,
    path: "/api/whatsapp/groups",
    Query: ListWhatsAppGroupsQuerySchema,
    Response: ApiSuccessSchema(ListWhatsAppGroupsDataSchema)
  },

  /** GET /api/whatsapp/groups/:groupId/invite-link — fetch the join link. */
  GetInviteLink: {
    method: "GET" as const,
    path: "/api/whatsapp/groups/:groupId/invite-link",
    PathVars: WhatsAppGroupPathVarsSchema,
    Query: GroupConnectionQuerySchema,
    Response: ApiSuccessSchema(InviteLinkDataSchema)
  },

  /** DELETE /api/whatsapp/groups/:groupId/invite-link — revoke the join link. */
  RevokeInviteLink: {
    method: "DELETE" as const,
    path: "/api/whatsapp/groups/:groupId/invite-link",
    PathVars: WhatsAppGroupPathVarsSchema,
    Body: GroupConnectionBodySchema,
    Response: ApiSuccessSchema(RevokeInviteLinkDataSchema)
  },

  /** POST /api/whatsapp/groups/:groupId/participants — add participants. */
  AddParticipants: {
    method: "POST" as const,
    path: "/api/whatsapp/groups/:groupId/participants",
    PathVars: WhatsAppGroupPathVarsSchema,
    Body: GroupParticipantsBodySchema,
    Response: ApiSuccessSchema(GroupParticipantsDataSchema)
  },

  /** DELETE /api/whatsapp/groups/:groupId/participants — remove participants. */
  RemoveParticipants: {
    method: "DELETE" as const,
    path: "/api/whatsapp/groups/:groupId/participants",
    PathVars: WhatsAppGroupPathVarsSchema,
    Body: GroupParticipantsBodySchema,
    Response: ApiSuccessSchema(GroupParticipantsDataSchema)
  },

  /** GET /api/whatsapp/groups/:groupId/join-requests — list pending join requests. */
  ListJoinRequests: {
    method: "GET" as const,
    path: "/api/whatsapp/groups/:groupId/join-requests",
    PathVars: WhatsAppGroupPathVarsSchema,
    Query: ListJoinRequestsQuerySchema,
    Response: ApiSuccessSchema(ListJoinRequestsDataSchema)
  },

  /** POST /api/whatsapp/groups/:groupId/join-requests/approve — approve joins. */
  ApproveJoinRequests: {
    method: "POST" as const,
    path: "/api/whatsapp/groups/:groupId/join-requests/approve",
    PathVars: WhatsAppGroupPathVarsSchema,
    Body: JoinRequestIdsBodySchema,
    Response: ApiSuccessSchema(ApproveJoinRequestsDataSchema)
  },

  /** POST /api/whatsapp/groups/:groupId/join-requests/reject — reject joins. */
  RejectJoinRequests: {
    method: "POST" as const,
    path: "/api/whatsapp/groups/:groupId/join-requests/reject",
    PathVars: WhatsAppGroupPathVarsSchema,
    Body: JoinRequestIdsBodySchema,
    Response: ApiSuccessSchema(RejectJoinRequestsDataSchema)
  }
} as const;
